import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Pill, RefreshCw, Pencil, Undo2, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ipdApi } from "@/api/ipd";
import { pharmacyApi } from "@/api/pharmacy";
import { ipdBillingApi } from "@/api/ipdBilling";
import type { IPDBill, IPDChargeableItem, PaymentMethod } from "@/types/ipdBilling";
import type { IPDAdmission } from "@/types/ipd";
import {
  isPharmaIncludedInPackage,
  isPackagePatient,
  packageTypeLabel,
  resolvePackageType,
} from "@/lib/ipdPackageTypes";
import { format } from "date-fns";
import ViewBill from "@/components/billing/ViewBill";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const PAYMENT_METHODS: PaymentMethod[] = [
  "CASH",
  "CARD",
  "UPI",
  "BANK_TRANSFER",
  "CHEQUE",
  "OTHER",
];

interface DrugSummary {
  id: string;
  name: string;
  strength?: string;
  form?: string;
}

function pharmaStatusBadge(status?: string | null) {
  const st = status ?? "PENDING";
  if (st === "PENDING") {
    return (
      <Badge variant="outline" className="text-amber-800 border-amber-700 bg-amber-50">
        Pending collection
      </Badge>
    );
  }
  if (st === "PAID_AT_PHARMACY") {
    return (
      <Badge variant="outline" className="text-green-800 border-green-700 bg-green-50">
        Paid at pharmacy
      </Badge>
    );
  }
  if (st === "PAID_AT_RECEPTION") {
    return (
      <Badge variant="outline" className="text-blue-800 border-blue-700 bg-blue-50">
        Billing done by receptionist
      </Badge>
    );
  }
  return <Badge variant="outline">{st}</Badge>;
}

function packageAutoPaidBadge() {
  return (
    <Badge variant="outline" className="text-slate-700 border-slate-400 bg-slate-50">
      Automatically paid (Package patient)
    </Badge>
  );
}

function formatCurrency(n: number) {
  return `₹${Number(n).toFixed(2)}`;
}

function clampPct(n: number) {
  return Math.min(100, Math.max(0, n));
}

function discountPctValue(c: IPDChargeableItem): number {
  const p = c.pharmacistDiscountPercent;
  if (typeof p !== "number" || !Number.isFinite(p)) return 0;
  return clampPct(p);
}

function lineNetAfterDiscount(c: IPDChargeableItem) {
  const gross = c.quantity * c.unitRate;
  const pct = discountPctValue(c);
  return Math.round(gross * (1 - pct / 100) * 100) / 100;
}

function displayDrugName(c: IPDChargeableItem) {
  const d = c.pharmacyIssue?.drug;
  if (d?.name) {
    return `${d.name}${d.strength ? ` ${d.strength}` : ""}${d.form ? ` (${d.form})` : ""}`.trim();
  }
  return c.description;
}

export default function PharmacistIPDPharmaPage() {
  const queryClient = useQueryClient();
  const [patientMode, setPatientMode] = useState<"admitted" | "discharged_package">("admitted");
  const [admissionId, setAdmissionId] = useState<string>("");
  const [dischargedSearch, setDischargedSearch] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [requestOpen, setRequestOpen] = useState(false);

  const [drugSearch, setDrugSearch] = useState("");
  const [drugResults, setDrugResults] = useState<DrugSummary[]>([]);
  const [drugLoading, setDrugLoading] = useState(false);
  const [pickedDrug, setPickedDrug] = useState<DrugSummary | null>(null);
  const [issueQty, setIssueQty] = useState("1");
  const [duplicateIssueConfirmOpen, setDuplicateIssueConfirmOpen] = useState(false);
  const issueSubmitLockRef = useRef(false);

  const { data: pickedDrugStock } = useQuery({
    queryKey: ["pharmacy-stock", pickedDrug?.id],
    queryFn: async () => {
      if (!pickedDrug?.id) return 0;
      const res = await pharmacyApi.getStock(pickedDrug.id);
      return Number(res.data?.data?.quantity ?? 0);
    },
    enabled: !!pickedDrug?.id,
  });

  const issueQtyNum = parseInt(issueQty, 10) || 0;
  const issueOverStock =
    pickedDrug != null &&
    pickedDrugStock != null &&
    issueQtyNum > 0 &&
    issueQtyNum > pickedDrugStock;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [collectOpen, setCollectOpen] = useState(false);
  const [collectMode, setCollectMode] = useState<PaymentMethod>("CASH");

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<IPDChargeableItem | null>(null);
  const [editForm, setEditForm] = useState({ description: "", serviceDate: "", quantity: "" });
  /** Local strings while editing per-row discount % (committed on blur). */
  const [discDraft, setDiscDraft] = useState<Record<string, string>>({});

  const [returnOpen, setReturnOpen] = useState(false);
  const [returnRow, setReturnRow] = useState<IPDChargeableItem | null>(null);
  const [returnQty, setReturnQty] = useState("1");

  const [receiptBillId, setReceiptBillId] = useState<string | null>(null);
  const [billModuleOpen, setBillModuleOpen] = useState(false);

  const admissionsQuery = useQuery({
    queryKey: ["ipd-admissions-admitted-pharma"],
    queryFn: async () => {
      const res = await ipdApi.getAdmissions({ status: "ADMITTED" });
      return (res.data as { data?: IPDAdmission[] })?.data ?? [];
    },
    enabled: patientMode === "admitted",
  });

  const dischargedPackagedQuery = useQuery({
    queryKey: ["ipd-discharged-packaged-pharma", dischargedSearch],
    queryFn: async () => {
      const res = await pharmacyApi.listDischargedPackagedAdmissions(dischargedSearch.trim() || undefined);
      return res.data?.data ?? [];
    },
    enabled: patientMode === "discharged_package",
  });

  const postDischargeAccessQuery = useQuery({
    queryKey: ["ipd-post-discharge-pharma-access", admissionId],
    queryFn: async () => {
      const res = await pharmacyApi.getPostDischargePharmaAccess(admissionId);
      return res.data?.data;
    },
    enabled: patientMode === "discharged_package" && !!admissionId,
  });

  const postDischargeUnlocked = postDischargeAccessQuery.data?.locked === false;
  const postDischargeLocked = patientMode === "discharged_package" && !postDischargeUnlocked;

  const selectedAdmission = useMemo(() => {
    if (patientMode === "discharged_package") {
      const row = (dischargedPackagedQuery.data ?? []).find((a) => a.admissionId === admissionId);
      if (!row) return null;
      return {
        id: row.admissionId,
        totalPackageCost: 1,
        packageType: "PACKAGE_1" as const,
        queue: {
          ipdNumber: row.ipdNumber,
          patient: { name: row.patientName, uhid: row.uhid ?? undefined },
        },
      } as IPDAdmission;
    }
    const admissions = admissionsQuery.data ?? [];
    return admissions.find((a) => a.id === admissionId) ?? null;
  }, [admissionsQuery.data, dischargedPackagedQuery.data, admissionId, patientMode]);

  const isPackagePatientFlag = useMemo(() => {
    if (patientMode === "discharged_package") return !!admissionId;
    return isPackagePatient(selectedAdmission?.totalPackageCost);
  }, [selectedAdmission, patientMode, admissionId]);

  const packageType = useMemo(() => resolvePackageType(selectedAdmission), [selectedAdmission]);

  /** Source of truth for issued medicines (always returned, even when IPD bill is not DRAFT). */
  const chargeablesQuery = useQuery({
    queryKey: ["ipd-pharma-chargeables", admissionId],
    enabled: !!admissionId,
    queryFn: async () => {
      const r = await ipdBillingApi.listChargeableItems(admissionId, "PHARMA");
      return r.data?.data ?? [];
    },
  });

  /** Bill status + accrued line nets when accrual has run (DRAFT). */
  const billQuery = useQuery({
    queryKey: ["ipd-pharma-bill-lines", admissionId],
    enabled: !!admissionId,
    queryFn: async () => {
      await ipdBillingApi.getOrCreateBill(admissionId);
      try {
        const r = await ipdBillingApi.getBillDetails(admissionId);
        return r.data?.data ?? null;
      } catch {
        return null;
      }
    },
  });

  const chargeableDrugId = (c: IPDChargeableItem) =>
    c.drugId ?? c.pharmacyIssue?.drug?.id ?? null;

  const existingLinesForPickedDrug = useMemo(() => {
    if (!pickedDrug?.id) return [];
    return (chargeablesQuery.data ?? []).filter(
      (c) => c.chargeCode !== "OT_PHARMA" && chargeableDrugId(c) === pickedDrug.id
    );
  }, [pickedDrug?.id, chargeablesQuery.data]);

  const priorIssuedQtyForPickedDrug = useMemo(
    () => existingLinesForPickedDrug.reduce((sum, c) => sum + Number(c.quantity || 0), 0),
    [existingLinesForPickedDrug]
  );

  const mergedRows = useMemo(() => {
    const chargeables = chargeablesQuery.data ?? [];
    const bill: IPDBill | null | undefined = billQuery.data ?? undefined;
    const netByChargeableId = new Map<string, number>();
    if (bill?.billItems?.length) {
      for (const bi of bill.billItems) {
        if (bi.sourceType === "PHARMA" && bi.sourceId) {
          netByChargeableId.set(bi.sourceId, bi.netAmount);
        }
      }
    }
    // OT consumption (theatre) is billed on the IPD bill at reception, not in this pharmacy IPD list
    return chargeables
      .filter((c) => c.chargeCode !== "OT_PHARMA")
      .map((c) => ({
        chargeable: c,
        billNet: netByChargeableId.get(c.id),
        estimated: lineNetAfterDiscount(c),
      }));
  }, [chargeablesQuery.data, billQuery.data]);

  const pharmaTotals = useMemo(() => {
    let totalMrp = 0;
    let totalNet = 0;
    for (const row of mergedRows) {
      const c = row.chargeable;
      const mrp = c.quantity * c.unitRate;
      const net = row.billNet == null ? row.estimated : row.billNet;
      totalMrp += mrp;
      totalNet += net;
    }
    const totalDiscount = totalMrp - totalNet;
    return {
      totalMrp: Math.round(totalMrp * 100) / 100,
      totalDiscount: Math.round(totalDiscount * 100) / 100,
      totalNet: Math.round(totalNet * 100) / 100,
    };
  }, [mergedRows]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const q = drugSearch.trim();
      if (q.length < 2) {
        setDrugResults([]);
        return;
      }
      setDrugLoading(true);
      try {
        const res = await api.get<{ data?: DrugSummary[] }>("/api/prescription/drugs", {
          params: { search: q },
        });
        setDrugResults(res.data?.data ?? []);
      } catch {
        setDrugResults([]);
      } finally {
        setDrugLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [drugSearch]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["ipd-pharma-chargeables", admissionId] });
    queryClient.invalidateQueries({ queryKey: ["ipd-pharma-bill-lines", admissionId] });
    queryClient.invalidateQueries({ queryKey: ["ipd-billing"] });
  };

  const requestMut = useMutation({
    mutationFn: async () => {
      if (!admissionId) throw new Error("Select a discharged patient");
      const reason = requestReason.trim();
      if (reason.length < 10) throw new Error("Reason must be at least 10 characters");
      await pharmacyApi.createPostDischargePharmaRequest({ admissionId, reason });
    },
    onSuccess: () => {
      toast.success("Request sent to hospital admin for approval");
      setRequestOpen(false);
      setRequestReason("");
      queryClient.invalidateQueries({ queryKey: ["ipd-discharged-packaged-pharma"] });
      queryClient.invalidateQueries({ queryKey: ["ipd-post-discharge-pharma-access", admissionId] });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || e?.message || "Could not submit request");
    },
  });

  const issueMut = useMutation({
    mutationFn: async () => {
      if (issueSubmitLockRef.current) {
        throw new Error("Issue already in progress — please wait");
      }
      issueSubmitLockRef.current = true;
      try {
        if (!admissionId) throw new Error("Select an admission");
        if (!pickedDrug) throw new Error("Select a medicine");
        const q = parseInt(issueQty, 10);
        if (!q || q < 1) throw new Error("Enter a valid quantity");
        if (pickedDrugStock != null && q > pickedDrugStock) {
          throw new Error(`Only ${pickedDrugStock} unit(s) available in stock`);
        }
        await pharmacyApi.issueIPDDirect({
          admissionId,
          items: [{ drugId: pickedDrug.id, quantity: q }],
        });
      } finally {
        issueSubmitLockRef.current = false;
      }
    },
    onSuccess: () => {
      toast.success("Medicines added to IPD bill");
      setDuplicateIssueConfirmOpen(false);
      setPickedDrug(null);
      setIssueQty("1");
      setDrugSearch("");
      setDrugResults([]);
      invalidateAll();
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message || e?.message || "Issue failed";
      if (!String(msg).includes("already in progress")) {
        toast.error(msg);
      }
    },
  });

  const requestIssueToIpd = () => {
    if (issueMut.isPending || issueSubmitLockRef.current || issueOverStock || issueQtyNum < 1) {
      return;
    }
    if (existingLinesForPickedDrug.length > 0) {
      setDuplicateIssueConfirmOpen(true);
      return;
    }
    issueMut.mutate();
  };

  const settleMut = useMutation({
    mutationFn: async () => {
      if (!admissionId) throw new Error("No admission");
      const ids = [...selectedIds].filter((id) => {
        const c = (chargeablesQuery.data ?? []).find((x) => x.id === id);
        if (!c) return false;
        const pending = !c.pharmaBillingStatus || c.pharmaBillingStatus === "PENDING";
        const includedInPackage = isPharmaIncludedInPackage(selectedAdmission, c.billSeparately);
        return pending && !includedInPackage;
      });
      if (!ids.length) throw new Error("Select at least one pending line");
      const res = await pharmacyApi.settleIPDPharmaAtPharmacy({
        admissionId,
        chargeableIds: ids,
        paymentMethod: collectMode,
      });
      return res.data?.data;
    },
    onSuccess: (payload) => {
      toast.success("Payment recorded");
      setCollectOpen(false);
      setSelectedIds(new Set());
      invalidateAll();
      const bid = payload?.receiptBillId;
      if (bid) {
        setReceiptBillId(bid);
        setBillModuleOpen(true);
      }
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || e?.message || "Settlement failed");
    },
  });

  const discountMut = useMutation({
    mutationFn: async ({ id, pct }: { id: string; pct: number }) => {
      await ipdBillingApi.updateChargeableItem(id, { pharmacistDiscountPercent: pct });
    },
    onSuccess: () => {
      invalidateAll();
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || e?.message || "Could not save discount");
    },
  });

  const returnMut = useMutation({
    mutationFn: async () => {
      if (!returnRow) throw new Error("No row");
      const issued = Math.floor(Number(returnRow.quantity) || 0);
      const qty = Math.floor(Number(returnQty));
      if (!qty || qty < 1) throw new Error("Enter a valid return quantity");
      if (qty > issued) throw new Error("Return quantity cannot exceed issued quantity");
      if (qty === issued) {
        await pharmacyApi.removeIPDPharmaLine(returnRow.id);
      } else {
        await pharmacyApi.reduceIPDPharmaLineQuantity(returnRow.id, issued - qty);
      }
    },
    onSuccess: () => {
      toast.success("Medicine returned — stock restored and IPD bill updated");
      setSelectedIds(new Set());
      setReturnOpen(false);
      setReturnRow(null);
      invalidateAll();
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || e?.message || "Could not return medicine");
    },
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      if (!editRow) throw new Error("No row");
      const desc = editForm.description.trim();
      const svc = editForm.serviceDate ? new Date(editForm.serviceDate).toISOString() : undefined;

      const isStockIssued = !!editRow.pharmacyIssue;
      const billIsDraft = billQuery.data?.status === "DRAFT";
      const pending = !editRow.pharmaBillingStatus || editRow.pharmaBillingStatus === "PENDING";
      const receptionBilled = editRow.pharmaBillingStatus === "PAID_AT_RECEPTION";
      const canEditQty =
        pending ||
        (patientMode === "admitted" && receptionBilled && billIsDraft) ||
        (patientMode === "discharged_package" &&
          postDischargeUnlocked &&
          receptionBilled);

      // Always allow description/date edit via billing API
      await ipdBillingApi.updateChargeableItem(editRow.id, {
        description: desc,
        serviceDate: svc,
      });

      // Quantity reduction for stock-issued lines must go via pharmacy API to maintain inventory
      if (isStockIssued) {
        const q = parseInt(editForm.quantity, 10);
        if (!q || q < 1) throw new Error("Enter a valid quantity");
        if (!canEditQty) throw new Error("Only pending or reception-billed lines can be reduced while admitted");
        if (!billIsDraft && patientMode !== "discharged_package") {
          throw new Error("Only while IPD bill is DRAFT (ask reception)");
        }
        if (q > editRow.quantity) {
          throw new Error("To increase quantity, remove the line and issue again");
        }
        if (q < editRow.quantity) {
          await pharmacyApi.reduceIPDPharmaLineQuantity(editRow.id, q);
        }
      }
    },
    onSuccess: () => {
      toast.success("Line updated");
      setEditOpen(false);
      setEditRow(null);
      invalidateAll();
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || e?.message || "Update failed");
    },
  });

  const toggleRow = (chargeableId: string, checked: boolean, pending: boolean, includedInPackage: boolean) => {
    if (!pending || includedInPackage) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(chargeableId);
      else next.delete(chargeableId);
      return next;
    });
  };

  const billStatus = billQuery.data?.status;

  const admissionLabel = (a: IPDAdmission) => {
    const name = a.queue?.patient?.name ?? "Patient";
    const ipdNo = a.queue?.ipdNumber ?? "";
    const bed = a.bedNumber ? `Bed ${a.bedNumber}` : "";
    return [name, ipdNo, bed].filter(Boolean).join(" · ");
  };

  const openEdit = (c: IPDChargeableItem) => {
    setEditRow(c);
    setEditForm({
      description: c.description ?? "",
      serviceDate: c.serviceDate
        ? format(new Date(c.serviceDate), "yyyy-MM-dd'T'HH:mm")
        : "",
      quantity: String(c.quantity ?? 1),
    });
    setEditOpen(true);
  };

  const canReturnLine = (c: IPDChargeableItem) => {
    const pending = !c.pharmaBillingStatus || c.pharmaBillingStatus === "PENDING";
    const receptionBilled = c.pharmaBillingStatus === "PAID_AT_RECEPTION";
    if (patientMode === "admitted") {
      return (pending || receptionBilled) && (!billStatus || billStatus === "DRAFT");
    }
    return postDischargeUnlocked && (pending || receptionBilled);
  };

  const returnDisabledReason = (c: IPDChargeableItem) => {
    if (patientMode === "discharged_package" && !postDischargeUnlocked) {
      return "Patient is discharged — request admin approval to return medicines";
    }
    if (c.pharmaBillingStatus === "PAID_AT_PHARMACY") {
      return "Paid at pharmacy — reverse collection before returning";
    }
    if (patientMode === "admitted" && billStatus && billStatus !== "DRAFT") {
      return "Only while IPD bill is DRAFT (ask reception)";
    }
    if (!canReturnLine(c)) {
      return "This line cannot be returned";
    }
    return "Return medicine to stock and adjust IPD bill";
  };

  const openReturn = (c: IPDChargeableItem) => {
    setReturnRow(c);
    setReturnQty("1");
    setReturnOpen(true);
  };

  const returnPreviewNet =
    returnRow && Math.floor(Number(returnQty)) > 0
      ? (() => {
          const qty = Math.min(
            Math.floor(Number(returnQty)) || 0,
            Math.floor(Number(returnRow.quantity) || 0)
          );
          const unitNet =
            returnRow.quantity > 0 ? lineNetAfterDiscount(returnRow) / returnRow.quantity : 0;
          return Math.round(unitNet * qty * 100) / 100;
        })()
      : 0;

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2">
        <Pill className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">IPD Pharma</h1>
          <p className="text-sm text-muted-foreground">
            Issue medicines to admitted patients and collect payment when needed. Return medicines against each line
            while the patient is still admitted (IPD bill must be DRAFT) — the final bill updates automatically. After
            discharge, request admin approval to issue or return medicines for Package 1 patients.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Select patient (admission)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs
            value={patientMode}
            onValueChange={(v) => {
              setPatientMode(v as "admitted" | "discharged_package");
              setAdmissionId("");
              setSelectedIds(new Set());
            }}
          >
            <TabsList>
              <TabsTrigger value="admitted">In-patients</TabsTrigger>
              <TabsTrigger value="discharged_package">Discharged (Package 1)</TabsTrigger>
            </TabsList>
            <TabsContent value="admitted" className="mt-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="min-w-[280px] flex-1 space-y-2">
                  <Label>In-patient</Label>
                  <Select
                    value={admissionId || undefined}
                    onValueChange={(v) => {
                      setAdmissionId(v);
                      setSelectedIds(new Set());
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an admitted patient…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(admissionsQuery.data ?? []).map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {admissionLabel(a)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!admissionId || chargeablesQuery.isFetching || billQuery.isFetching}
                  onClick={() => invalidateAll()}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-1 ${chargeablesQuery.isFetching || billQuery.isFetching ? "animate-spin" : ""}`}
                  />
                  Refresh
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="discharged_package" className="mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Only discharged patients on Package 1 (everything included). Medicines are hospital cost and do not change
                the patient bill amount.
              </p>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="min-w-[200px] flex-1 space-y-2">
                  <Label>Search by name, UHID, or IPD number</Label>
                  <Input
                    placeholder="Type to search…"
                    value={dischargedSearch}
                    onChange={(e) => setDischargedSearch(e.target.value)}
                  />
                </div>
                <div className="min-w-[280px] flex-1 space-y-2">
                  <Label>Discharged package patient</Label>
                  <Select
                    value={admissionId || undefined}
                    onValueChange={(v) => {
                      setAdmissionId(v);
                      setSelectedIds(new Set());
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a discharged patient…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(dischargedPackagedQuery.data ?? []).map((a) => (
                        <SelectItem key={a.admissionId} value={a.admissionId}>
                          {a.patientName} · {a.ipdNumber}
                          {a.access === "UNLOCKED" ? " · Open" : a.access === "PENDING" ? " · Pending" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={dischargedPackagedQuery.isFetching}
                  onClick={() => dischargedPackagedQuery.refetch()}
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${dischargedPackagedQuery.isFetching ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>
              {admissionId && postDischargeAccessQuery.data && (
                <div
                  className={`rounded-lg border px-4 py-3 text-sm ${
                    postDischargeUnlocked
                      ? "border-emerald-300 bg-emerald-50 text-emerald-950"
                      : "border-amber-300 bg-amber-50 text-amber-950"
                  }`}
                >
                  {postDischargeUnlocked ? (
                    <p>
                      Admin approved — you can issue or return medicines until{" "}
                      {postDischargeAccessQuery.data.unlockedUntil
                        ? format(new Date(postDischargeAccessQuery.data.unlockedUntil), "dd MMM yyyy, hh:mm a")
                        : "the unlock window ends"}
                      .
                    </p>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p>{postDischargeAccessQuery.data.reason}</p>
                      {!postDischargeAccessQuery.data.pendingRequest && (
                        <Button type="button" size="sm" variant="secondary" onClick={() => setRequestOpen(true)}>
                          <Send className="h-4 w-4 mr-1" />
                          Request to open
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {admissionId && !postDischargeLocked && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Add medicines</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative space-y-2">
                <Label>Search medicine</Label>
                <Input
                  placeholder="Type name (min 2 characters)…"
                  value={drugSearch}
                  onChange={(e) => setDrugSearch(e.target.value)}
                />
                {drugLoading && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                  </div>
                )}
                {drugResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-md border bg-popover shadow-md">
                    {drugResults.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                        onClick={() => {
                          setPickedDrug(d);
                          setDrugResults([]);
                          setDrugSearch(`${d.name} ${d.strength ?? ""}`);
                        }}
                      >
                        {d.name}{" "}
                        <span className="text-muted-foreground">{d.strength ?? ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {pickedDrug && existingLinesForPickedDrug.length > 0 && (
                <div
                  className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
                  role="status"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-700" />
                  <p>
                    <strong>Already on this IPD:</strong> {pickedDrug.name} was issued{" "}
                    {existingLinesForPickedDrug.length} time
                    {existingLinesForPickedDrug.length === 1 ? "" : "s"} (total qty{" "}
                    {priorIssuedQtyForPickedDrug}). You may add again if clinically needed — confirm when
                    issuing.
                  </p>
                </div>
              )}
              {pickedDrug && (
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-2">
                    <Label>Selected</Label>
                    <div className="text-sm font-medium">
                      {pickedDrug.name} {pickedDrug.strength ?? ""}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="qty">Quantity</Label>
                    <Input
                      id="qty"
                      className={`w-24 ${issueOverStock ? "border-rose-400" : ""}`}
                      type="number"
                      min={1}
                      value={issueQty}
                      onChange={(e) => setIssueQty(e.target.value)}
                    />
                    {pickedDrugStock != null && (
                      <p className={`text-xs ${issueOverStock ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>
                        Available in stock: {pickedDrugStock}
                        {issueOverStock && ` — reduce qty to ${pickedDrugStock} or less`}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={requestIssueToIpd}
                    disabled={issueMut.isPending || issueOverStock || issueQtyNum < 1}
                    title="Click once — wait until the issue completes"
                  >
                    {issueMut.isPending ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Issuing…
                      </span>
                    ) : (
                      "Issue to IPD bill"
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-lg">Issued medicines (IPD pharmacy)</CardTitle>
              {patientMode !== "discharged_package" && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!selectedIds.size || settleMut.isPending}
                  onClick={() => setCollectOpen(true)}
                >
                  Collect payment (selected)
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground border-l-2 border-primary/30 pl-3">
                OT consumption (theatre) lines are not listed here — they are added to the patient&apos;s IPD bill at
                reception (and excluded from the package bill when the case is a package).
              </p>
              {isPackagePatientFlag && packageType && (
                <p className="text-sm text-muted-foreground border-l-2 border-slate-300 pl-3">
                  {packageTypeLabel(packageType)}.{" "}
                  {packageType === "PACKAGE_1" || packageType === "PACKAGE_3" ? (
                    <>
                      Medicines included in the package show as{" "}
                      <strong className="text-foreground">Automatically paid</strong> unless reception marks a line as{" "}
                      <strong className="text-foreground">Bill separately</strong>.
                    </>
                  ) : (
                    <>
                      Medicines are <strong className="text-foreground">billed separately</strong> — collect payment here
                      or at reception like a non-package patient.
                    </>
                  )}
                </p>
              )}
              <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                <span>
                  Bill status:{" "}
                  <strong className="text-foreground">{billStatus ?? "—"}</strong>
                </span>
                {billStatus && billStatus !== "DRAFT" && (
                  <span className="text-amber-800">
                    Accrual is paused while the bill is not DRAFT — amounts below may show as estimates until billing syncs.
                  </span>
                )}
              </div>

              {chargeablesQuery.isLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading issued medicines…
                </div>
              ) : !mergedRows.length ? (
                <p className="text-muted-foreground text-sm">No pharmacy medicines issued for this admission yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]" />
                      <TableHead>Medicine</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right w-[92px]">Disc %</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                      <TableHead>Batch</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mergedRows.map(({ chargeable: c, billNet, estimated }) => {
                      const id = c.id;
                      const pending = !c.pharmaBillingStatus || c.pharmaBillingStatus === "PENDING";
                      const includedInPackage = isPharmaIncludedInPackage(selectedAdmission, c.billSeparately);
                      const showAutoPaid = pending && includedInPackage;
                      const showEst = billNet == null;
                      const amount = showEst ? estimated : billNet;
                      const batchNo = c.pharmacyIssue?.batch?.batchNo ?? "—";
                      return (
                        <TableRow key={id}>
                          <TableCell>
                            {pending && !includedInPackage ? (
                              <Checkbox
                                checked={selectedIds.has(id)}
                                onCheckedChange={(check) =>
                                  toggleRow(id, check === true, pending, includedInPackage)
                                }
                              />
                            ) : null}
                          </TableCell>
                          <TableCell className="font-medium">{displayDrugName(c)}</TableCell>
                          <TableCell className="text-right">{c.quantity}</TableCell>
                          <TableCell className="text-right">{formatCurrency(c.unitRate)}</TableCell>
                          <TableCell className="text-right p-1 align-middle">
                            <div className="flex flex-col items-end gap-0.5">
                              <Input
                                className="h-8 w-[76px] text-right tabular-nums text-sm px-2"
                                type="number"
                                min={0}
                                max={100}
                                step={0.5}
                                disabled={
                                  !pending ||
                                  billStatus !== "DRAFT" ||
                                  discountMut.isPending
                                }
                                title={
                                  billStatus !== "DRAFT"
                                    ? "Discount can be changed only while IPD bill is DRAFT"
                                    : undefined
                                }
                                value={
                                  discDraft[id] !== undefined
                                    ? discDraft[id]
                                    : String(discountPctValue(c))
                                }
                                onChange={(e) =>
                                  setDiscDraft((prev) => ({ ...prev, [id]: e.target.value }))
                                }
                                onBlur={() => {
                                  const raw = discDraft[id];
                                  if (raw === undefined) return;
                                  const trimmed = raw.trim();
                                  const num =
                                    trimmed === "" ? 0 : parseFloat(trimmed.replace(",", "."));
                                  setDiscDraft((prev) => {
                                    const next = { ...prev };
                                    delete next[id];
                                    return next;
                                  });
                                  if (!Number.isFinite(num)) {
                                    toast.error("Enter a valid discount %");
                                    return;
                                  }
                                  const clamped = clampPct(num);
                                  if (Math.abs(clamped - discountPctValue(c)) < 1e-9) return;
                                  discountMut.mutate({ id, pct: clamped });
                                }}
                              />
                              {billStatus && billStatus !== "DRAFT" && (
                                <span className="text-[10px] text-muted-foreground text-right leading-tight max-w-[104px]">
                                  Editable only when IPD bill is DRAFT — ask reception.
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end gap-0.5">
                              <span>{formatCurrency(amount)}</span>
                              <span className="text-[10px] text-muted-foreground">(GST extra)</span>
                              {showEst && (
                                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                                  Estimated
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{batchNo}</TableCell>
                          <TableCell>{showAutoPaid ? packageAutoPaidBadge() : pharmaStatusBadge(c.pharmaBillingStatus)}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                title="Edit description / date"
                                onClick={() => openEdit(c)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-amber-800 hover:text-amber-900 disabled:opacity-40"
                                disabled={returnMut.isPending || !canReturnLine(c)}
                                title={returnDisabledReason(c)}
                                aria-label="Return medicine"
                                onClick={() => openReturn(c)}
                              >
                                <Undo2 className="h-4 w-4 mr-1" />
                                Return
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter className="border-t bg-muted/40 font-medium text-foreground hover:bg-muted/40">
                    <TableRow>
                      <TableCell />
                      <TableCell colSpan={4} className="align-middle">
                        <span className="font-semibold">Totals</span>
                        <span className="text-xs font-normal text-muted-foreground ml-1.5">(excl. GST)</span>
                      </TableCell>
                      <TableCell className="text-right align-top py-3">
                        <div className="flex flex-col items-end gap-1 text-sm">
                          <div className="flex w-full max-w-[220px] justify-between gap-4 tabular-nums">
                            <span className="text-muted-foreground font-normal">Total at MRP</span>
                            <span>{formatCurrency(pharmaTotals.totalMrp)}</span>
                          </div>
                          <div className="flex w-full max-w-[220px] justify-between gap-4 tabular-nums">
                            <span className="text-muted-foreground font-normal">Discount</span>
                            <span>{formatCurrency(pharmaTotals.totalDiscount)}</span>
                          </div>
                          <div className="flex w-full max-w-[220px] justify-between gap-4 tabular-nums border-t border-border/60 pt-1 mt-0.5">
                            <span className="text-muted-foreground font-normal">After discount</span>
                            <span className="font-semibold">{formatCurrency(pharmaTotals.totalNet)}</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground font-normal">(GST extra)</span>
                        </div>
                      </TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  </TableFooter>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={duplicateIssueConfirmOpen} onOpenChange={setDuplicateIssueConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue same medicine again?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="flex gap-2 text-amber-950 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {pickedDrug?.name} is already on this IPD ({existingLinesForPickedDrug.length} line
                {existingLinesForPickedDrug.length === 1 ? "" : "s"}, qty {priorIssuedQtyForPickedDrug} total). Add
                only if you intend a separate issue.
              </span>
            </p>
            <p className="text-muted-foreground">
              Press <strong>Issue once</strong> below and wait — do not double-click while stock is updating.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDuplicateIssueConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={issueMut.isPending}
              onClick={() => issueMut.mutate()}
            >
              {issueMut.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Issuing…
                </span>
              ) : (
                "Issue once"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request post-discharge pharmacy access</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            After discharge, you cannot return or edit medicines directly. Hospital admin must approve before you can
            issue or return medicines for this Package 1 patient. Explain what you need to change.
          </p>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              placeholder="e.g. Return unused cannula and fixer issued before discharge…"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRequestOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => requestMut.mutate()} disabled={requestMut.isPending}>
              {requestMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={collectOpen} onOpenChange={setCollectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collect payment at pharmacy</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Payment mode</Label>
            <Select value={collectMode} onValueChange={(v) => setCollectMode(v as PaymentMethod)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Records a deposit and marks selected lines as paid at pharmacy. If collection fails while the bill is not DRAFT,
              ask reception to return the bill to DRAFT so lines match, then try again.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setCollectOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => settleMut.mutate()} disabled={settleMut.isPending}>
              {settleMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ViewBill
        billId={receiptBillId}
        isOpen={billModuleOpen}
        onClose={() => {
          setBillModuleOpen(false);
          setReceiptBillId(null);
        }}
      />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit line</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Prefer <strong>Return</strong> to put medicines back and adjust the IPD bill. Here, only{" "}
              <strong>quantity reduction</strong> is allowed for stock-issued lines. To increase quantity, return the
              line and issue again.
            </p>
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                min={1}
                value={editForm.quantity}
                onChange={(e) => setEditForm((f) => ({ ...f, quantity: e.target.value }))}
                disabled={!editRow?.pharmacyIssue}
              />
              {editRow?.pharmacyIssue && billStatus !== "DRAFT" && patientMode === "admitted" && (
                <p className="text-xs text-amber-800">Quantity can be changed only when bill status is DRAFT (ask reception).</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Service date</Label>
              <Input
                type="datetime-local"
                value={editForm.serviceDate}
                onChange={(e) => setEditForm((f) => ({ ...f, serviceDate: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={updateMut.isPending} onClick={() => updateMut.mutate()}>
              {updateMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return medicine</DialogTitle>
          </DialogHeader>
          {returnRow && (
            <div className="space-y-3">
              <p className="text-sm">
                <span className="font-medium">{displayDrugName(returnRow)}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Stock is restored and the final IPD bill is recalculated (when the bill is DRAFT). Available only while
                the patient is admitted — after discharge, request admin approval first.
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground text-xs">Issued qty</div>
                  <div className="font-medium">{returnRow.quantity}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Line net (excl. GST)</div>
                  <div className="font-medium">{formatCurrency(lineNetAfterDiscount(returnRow))}</div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="return-qty">Quantity to return</Label>
                <Input
                  id="return-qty"
                  type="number"
                  min={1}
                  max={returnRow.quantity}
                  value={returnQty}
                  onChange={(e) => setReturnQty(e.target.value)}
                />
              </div>
              {Math.floor(Number(returnQty)) > 0 && (
                <p className="text-sm rounded-md border bg-muted/40 px-3 py-2">
                  Bill reduction (est.):{" "}
                  <span className="font-semibold tabular-nums">{formatCurrency(returnPreviewNet)}</span>
                  <span className="text-xs text-muted-foreground ml-1">(excl. GST)</span>
                  {Math.floor(Number(returnQty)) >= returnRow.quantity && (
                    <span className="block text-xs text-muted-foreground mt-1">
                      Full return — this line will be removed from the IPD bill.
                    </span>
                  )}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setReturnOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={returnMut.isPending || !returnRow} onClick={() => returnMut.mutate()}>
              {returnMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
